// === Admin: SWMS Ranking Risk Backends ===
// Full CRUD page — list, add, edit, soft-delete.
// Ported from old Click2Gro (app/Controllers/Admin/SwmsRankingRiskBackendsController.php)
// Fields: risk_ranking_harm, likelihood_probability, consequence, description_impact,
//         colour_background, colour_text

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SwmsRankingRiskBackendRow {
  readonly id: number;
  readonly risk_ranking_harm: string | null;
  readonly likelihood_probability: string | null;
  readonly consequence: string | null;
  readonly description_impact: string | null;
  readonly colour_background: string | null;
  readonly colour_text: string | null;
  readonly created_at: string | null;
  readonly created_by: string | null;
  readonly updated_at: string | null;
  readonly updated_by: string | null;
}

interface FormState {
  risk_ranking_harm: string;
  likelihood_probability: string;
  consequence: string;
  description_impact: string;
  colour_background: string;
  colour_text: string;
  created_at: string | null;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

const DEFAULT_FORM: FormState = {
  risk_ranking_harm: '',
  likelihood_probability: '',
  consequence: '',
  description_impact: '',
  colour_background: '#ffffff',
  colour_text: '#000000',
  created_at: null,
  created_by: null,
  updated_at: null,
  updated_by: null,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtAuDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'short', timeStyle: 'medium', hour12: false }).format(new Date(iso));
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium' }).format(new Date(iso));
}

function truncate(text: string | null, max = 60): string {
  if (!text) return '—';
  return text.length <= max ? text : text.slice(0, max) + '…';
}

// ---------------------------------------------------------------------------
// SwmsRankingRiskBackendsPage
// ---------------------------------------------------------------------------

export function SwmsRankingRiskBackendsPage() {
  const { session } = useAuth();
  const navigate = useNavigate();

  // List state
  const [rows, setRows]               = useState<SwmsRankingRiskBackendRow[]>([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch]           = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);
  const [loading, setLoading]         = useState(false);
  const [listError, setListError]     = useState<string | null>(null);

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId]     = useState<number | null>(null);
  const [form, setForm]         = useState<FormState>(DEFAULT_FORM);
  const [saving, setSaving]     = useState(false);

  // Delete dialog
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false, msg: '', severity: 'success',
  });

  const token = session?.access_token ?? '';

  // -------------------------------------------------------------------------
  // Fetch list
  // -------------------------------------------------------------------------
  const fetchRows = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const params = new URLSearchParams({
        page:  String(page + 1),
        limit: String(rowsPerPage),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      const res = await fetch(`${getApiUrl()}/api/v1/admin/swms-ranking-risk-backends?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json() as { data: SwmsRankingRiskBackendRow[]; total: number };
      setRows(json.data ?? []);
      setTotal(json.total ?? 0);
    } catch {
      setListError('Failed to load SWMS ranking risks');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { void fetchRows(); }, [fetchRows]);

  // -------------------------------------------------------------------------
  // Form open/close
  // -------------------------------------------------------------------------
  const openAdd = () => {
    setEditId(null);
    setForm(DEFAULT_FORM);
    setFormOpen(true);
  };

  const openEdit = (row: SwmsRankingRiskBackendRow) => {
    setEditId(row.id);
    setForm({
      risk_ranking_harm:      row.risk_ranking_harm      ?? '',
      likelihood_probability: row.likelihood_probability ?? '',
      consequence:            row.consequence            ?? '',
      description_impact:     row.description_impact     ?? '',
      colour_background:      row.colour_background      ?? '#ffffff',
      colour_text:            row.colour_text            ?? '#000000',
      created_at:             row.created_at             ?? null,
      created_by:             row.created_by             ?? null,
      updated_at:             row.updated_at             ?? null,
      updated_by:             row.updated_by             ?? null,
    });
    setFormOpen(true);
  };

  const closeForm = () => { setFormOpen(false); };

  const setField = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // -------------------------------------------------------------------------
  // Save (add / edit)
  // -------------------------------------------------------------------------
  const handleSave = async () => {
    if (!form.risk_ranking_harm.trim()) return;
    setSaving(true);
    try {
      const url = editId
        ? `${getApiUrl()}/api/v1/admin/swms-ranking-risk-backends/${editId}`
        : `${getApiUrl()}/api/v1/admin/swms-ranking-risk-backends`;
      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          risk_ranking_harm:      form.risk_ranking_harm.trim(),
          likelihood_probability: form.likelihood_probability.trim() || null,
          consequence:            form.consequence.trim() || null,
          description_impact:     form.description_impact.trim() || null,
          colour_background:      form.colour_background || null,
          colour_text:            form.colour_text || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
        throw new Error(err.error ?? 'Save failed');
      }
      setSnack({ open: true, msg: editId ? 'Risk ranking updated' : 'Risk ranking added', severity: 'success' });
      closeForm();
      void fetchRows();
    } catch (e) {
      setSnack({ open: true, msg: e instanceof Error ? e.message : 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Delete
  // -------------------------------------------------------------------------
  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await fetch(`${getApiUrl()}/api/v1/admin/swms-ranking-risk-backends/${deleteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnack({ open: true, msg: 'Risk ranking deleted', severity: 'success' });
      setDeleteId(null);
      void fetchRows();
    } catch {
      setSnack({ open: true, msg: 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / rowsPerPage);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>SWMS Ranking Risk Backends</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage SWMS risk ranking levels and colour coding
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Risk Ranking
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search risk, likelihood or consequence…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 340 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>
                Clear
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell>Risk Ranking</AppTableHeadCell>
                  <AppTableHeadCell>Likelihood / Probability</AppTableHeadCell>
                  <AppTableHeadCell>Consequence</AppTableHeadCell>
                  <AppTableHeadCell>Description / Impact</AppTableHeadCell>
                  <AppTableHeadCell>Colours</AppTableHeadCell>
                  <AppTableHeadCell>Updated</AppTableHeadCell>
                  <AppTableHeadCell align="right">Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No risk rankings found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <AppTableRow key={row.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {truncate(row.risk_ranking_harm)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {truncate(row.likelihood_probability)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {truncate(row.consequence)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" color="text.secondary">
                          {truncate(row.description_impact, 80)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {row.colour_background && (
                            <Tooltip title={`BG: ${row.colour_background}`}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 1,
                                  bgcolor: row.colour_background,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  flexShrink: 0,
                                }}
                              />
                            </Tooltip>
                          )}
                          {row.colour_text && (
                            <Tooltip title={`Text: ${row.colour_text}`}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 1,
                                  bgcolor: row.colour_text,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  flexShrink: 0,
                                }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {fmtDate(row.updated_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </AppTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}
            >
              {loading ? 'Loading…' : total === 0 ? 'No records found'
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Show
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Add / Edit Dialog */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>{editId ? 'Edit Risk Ranking' : 'Add Risk Ranking'}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Stack spacing={2}>
            <TextField
              autoFocus
              label="Risk Ranking Harm"
              fullWidth
              size="small"
              value={form.risk_ranking_harm}
              onChange={(e) => setField('risk_ranking_harm', e.target.value)}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label="Likelihood / Probability"
              fullWidth
              size="small"
              value={form.likelihood_probability}
              onChange={(e) => setField('likelihood_probability', e.target.value)}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label="Consequence"
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={form.consequence}
              onChange={(e) => setField('consequence', e.target.value)}
            />
            <TextField
              label="Description / Impact"
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={form.description_impact}
              onChange={(e) => setField('description_impact', e.target.value)}
            />
            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Background Colour
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    component="input"
                    type="color"
                    value={form.colour_background}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setField('colour_background', e.target.value)
                    }
                    sx={{
                      width: 44,
                      height: 36,
                      padding: 0,
                      border: 'none',
                      outline: 'none',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: 'transparent',
                      overflow: 'hidden',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {form.colour_background}
                  </Typography>
                </Stack>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Text Colour
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    component="input"
                    type="color"
                    value={form.colour_text}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setField('colour_text', e.target.value)
                    }
                    sx={{
                      width: 44,
                      height: 36,
                      padding: 0,
                      border: 'none',
                      outline: 'none',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: 'transparent',
                      overflow: 'hidden',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {form.colour_text}
                  </Typography>
                </Stack>
              </Box>
              {/* Live preview */}
              <Box sx={{ ml: 'auto !important' }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Preview
                </Typography>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    bgcolor: form.colour_background,
                    color: '#000000',
                    border: '1px solid',
                    borderColor: 'divider',
                    minWidth: 100,
                    textAlign: 'center',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {form.risk_ranking_harm || 'Sample'}
                </Box>
              </Box>
            </Stack>
          </Stack>

          {/* Audit Trail */}
          {editId && (
            <Box
              mt={2}
              p={2}
              sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                Audit Trail
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(form.created_at)}{form.created_by ? <> &mdash; <strong>{form.created_by}</strong></> : ''}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(form.updated_at)}{form.updated_by ? <> &mdash; <strong>{form.updated_by}</strong></> : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeForm} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving || !form.risk_ranking_harm.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Risk Ranking</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this risk ranking? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
